import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorList, Field, OTPField } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'

/**
 * OTP-first staff login (no passwords — matches the propte products).
 * Rendered on the homepage hero. The email step submits via a fetcher so
 * the URL never changes (a plain form POST to the index route would leave
 * `?index` in the address bar); the hosting route must export an action
 * delegating to `loginAction` from `app/routes/_auth/login.server.ts`.
 */

export const MagicLinkLoginSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string().optional(),
})

export function LoginForm({ redirectTo }: { redirectTo: string | null }) {
	const fetcher = useFetcher<{
		result: any
		sentTo: string | null
	}>()
	const isSending = fetcher.state !== 'idle'
	const sentTo = fetcher.data?.sentTo
	// The code form is a real navigation to /verify (it redirects to the
	// portal on success and owns error display on failure).
	const isVerifying = useIsPending({ formAction: '/verify' })

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(MagicLinkLoginSchema),
		defaultValue: { redirectTo },
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MagicLinkLoginSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	if (sentTo) {
		return (
			<div className="flex flex-col items-center gap-3 py-4 text-center">
				<Icon name="envelope-closed" className="text-primary h-8 w-8" />
				<h3 className="text-lg font-semibold">Check your email</h3>
				<p className="text-muted-foreground text-sm">
					If <span className="font-medium">{sentTo}</span> has an account,
					we've emailed it a 6-character code (and a link, if that's easier).
					Both expire in 10 minutes.
				</p>
				{/* Codes beat links here: a link logs in whichever browser opens
				    the email; the code lands in THIS tab. Posts to /verify, which
				    owns validation + error display. */}
				<Form
					method="POST"
					action="/verify"
					className="flex w-full max-w-xs flex-col gap-2 pt-2 text-left"
				>
					<HoneypotInputs />
					<input type="hidden" name="type" value="login" />
					<input type="hidden" name="target" value={sentTo} />
					{redirectTo ? (
						<input type="hidden" name="redirectTo" value={redirectTo} />
					) : null}
					<OTPField
						labelProps={{ children: 'Code' }}
						inputProps={{
							name: 'code',
							autoComplete: 'one-time-code',
							autoFocus: true,
						}}
					/>
					<StatusButton
						className="w-full"
						status={isVerifying ? 'pending' : 'idle'}
						type="submit"
						disabled={isVerifying}
					>
						Log in
					</StatusButton>
				</Form>
				<button
					type="button"
					onClick={() => fetcher.load(window.location.pathname)}
					className="text-sm font-medium hover:underline"
				>
					Use a different address
				</button>
			</div>
		)
	}

	return (
		<fetcher.Form method="POST" {...getFormProps(form)}>
			<HoneypotInputs />
			<Field
				labelProps={{ children: 'Email' }}
				inputProps={{
					...getInputProps(fields.email, { type: 'email' }),
					autoFocus: true,
					className: 'lowercase',
					autoComplete: 'email',
					placeholder: 'you@dimplecolor.com',
				}}
				errors={fields.email.errors}
			/>

			<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
			<ErrorList errors={form.errors} id={form.errorId} />

			<div>
				<StatusButton
					className="w-full"
					status={isSending ? 'pending' : (form.status ?? 'idle')}
					type="submit"
					disabled={isSending}
				>
					Email me a login code
				</StatusButton>
			</div>
			<p className="text-muted-foreground mt-3 text-center text-xs">
				No passwords — we'll email you a one-time code.
			</p>
		</fetcher.Form>
	)
}
